import { Configuration, SkusApi } from '@/api';
import useFindSkusQuery from '@/api/queries/useFindSkusQuery';
import Button from '@/components/Button';
import Confirm from '@/components/Confirm';
import CustomToast from '@/components/CustomToast';
import SpinnerIcon from '@/components/icons/SpinnerIcon';
import Pagination from '@/components/Pagination';
import { SearchProps } from '@/components/search/useSearch';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import { ProductAddPath, ProductEditPath } from '@/modules/product/paths';
import isEmpty from '@/utils/object/isEmpty';
import optional from '@/utils/object/optional';
import toast from 'react-hot-toast';
import { Link } from 'react-router-dom';

interface ProductTableProps extends SearchProps {
  supplierId?: string | null;
  productCategoryId?: string | null;
  createdByStoreId?: string | null;
}

interface Sku {
  id: string;
  name: string;
  product?: {
    productCategory?: {
      name: string;
    };
  };
  createdByStoreId?: string;
}

const ProductTable = ({
  searchQuery,
  page = 1,
  onChangePage = () => {},
  supplierId,
  productCategoryId,
  createdByStoreId,
}: ProductTableProps) => {
  const { skus, isLoading, error, itemsPerPage, invalidateQuery } = useFindSkusQuery(
    page,
    searchQuery,
    {
      supplierId: optional(supplierId),
      productCategoryId: optional(productCategoryId),
      createdByStoreId: optional(createdByStoreId),
    },
    [
      { sortBy: 'categoryName', sortDirection: 'asc' },
      { sortBy: 'skuName', sortDirection: 'asc' },
    ],
  );

  const skusApi = new SkusApi(new Configuration(useAuth));

  const handleDeleteProduct = async (sku: Sku) => {
    try {
      await skusApi.deleteSku(sku.id);

      toast.custom((t) => (
        <CustomToast
          id={t.id}
          message={`Product "${sku.name}" removed successfully!`}
          visible={t.visible}
        />
      ));

      await invalidateQuery();
    } catch (error) {
      console.error('Error while deleting product:', error);
    }
  };

  if (!isLoading && !error && skus.items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-8">
        <p>No products found.</p>

        <Button to={ProductAddPath} variant="primary">
          Click here to add a new product
        </Button>
      </div>
    );
  }

  return (
    <div className="table-container" data-testid="product-list">
      <div className="table-wrapper">
        <table className={isLoading ? 'loading' : ''} data-testid="product-list-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Category</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {skus.items.map((sku: Sku) => {
              if (sku.id === undefined) {
                return;
              }

              const isAllowedToDelete =
                isEmpty(createdByStoreId) || createdByStoreId === sku.createdByStoreId;

              return (
                <tr
                  key={sku.id}
                  data-testid="product-row"
                  data-sku-id={sku.id}
                  data-sku-name={sku.name}
                >
                  <td className="w-1/2">
                    <Link
                      to={ProductEditPath.withId(sku.id)}
                      className="text-blue-500 hover:underline"
                      data-testid="product-row-link"
                    >
                      {sku.name}
                    </Link>
                  </td>
                  <td>{sku.product?.productCategory && sku.product?.productCategory.name}</td>
                  <td>
                    {isAllowedToDelete && (
                      <Confirm
                        onConfirm={() => handleDeleteProduct(sku)}
                        message={`Do you want to delete product: ${sku.name}?`}
                      >
                        {(handleClick) => (
                          <Button onClick={handleClick} variant="delete" size="xs">
                            Delete
                          </Button>
                        )}
                      </Confirm>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {isLoading ? (
          <div role="status" className="loader">
            <SpinnerIcon />
            <span className="sr-only">Loading...</span>
          </div>
        ) : (
          ''
        )}
      </div>
      <div className="table-bottom">
        <Pagination
          currentPage={page}
          itemsPerPage={itemsPerPage}
          totalCount={skus.totalCount || 0}
          estimatedTotalCount={skus.estimatedTotalCount || 0}
          onClick={onChangePage}
        />
      </div>
    </div>
  );
};

export default ProductTable;
